import type { FandomContext } from "common-types";

/**
 * Monetization tier level for a page.
 *
 * Tier 1 (TOS Violations):
 * - Pages and wikis that violate our platform's Terms of Service. (e.g., hate speech, extremist content, explicit pornography)
 * - Ad Logic: Not applicable as content is removed.
 *
 * Tier 2 (Complies with TOS, Not Suitable for Advertising):
 * - Pages that comply with our Terms of Service but are deemed unsuitable for any advertising (e.g., graphic depictions of in-game violence, mature character lore) . These pages may remain visible for reference.
 * - Action: No Odyssey script will be loaded.
 *
 * Tier 3 (Complies with TOS, Eligible for General Monetization ex-TTD):
 * - Pages that meet editorial, platform, and general brand safety standards, making them eligible for monetization with most demand sources, excluding TTD.
 * - Action: Monetized via Google AdX, Direct Campaigns, and Header Bidding (excluding TTD).
 * - GAM Key-Value: tier=3.
 *
 * Tier 4 (Complies with TOS, Eligible for All Monetization incl. TTD):
 * - Pages that meet all general standards and specific, more stringent requirements set by The Trade Desk.
 * - Action: Monetized via all available demand sources, including TTD, Google AdX, Direct Campaigns, and Header Bidding.
 * - GAM Key-Value: tier=4.
 *
 * @see {@link https://docs.google.com/document/d/1HJ5HCPGIRxXXkELuSmDulBTYYtrof-uA0LBVspYww2Q/edit?tab=t.0#heading=h.qggjh1l3ct6}
 *
 * @note Any other value than 3 and 4 will result in full demonetization of the page.
 * @note UCP will default to sending -1 ({@link MonetizationTier.ERROR_DISABLED_TIERS}) if the monetization tier is not enabled.
 * @note Odyssey defaults to -2 ({@link MonetizationTier.ERROR_NO_TIER_PROVIDED}) if the monetization tier is not set in UCP.
 * @note In case where the monetization tier is not set to 3 or 4, the page will be considered fully demonetized and Odyssey will break.
 * @note the values of the enum are strings in order to force TypeScript to use string enums and not const or numeric enums (@see {@link https://www.typescriptlang.org/docs/handbook/enums.html#computed-and-constant-members})
 *
 */
export enum MonetizationTier {
  ERROR_NO_TIER_PROVIDED = "-2",
  ERROR_DISABLED_TIERS = "-1",
  PARTIALLY_MONETIZED = "3",
  FULLY_MONETIZED = "4",
}

/**
 * Returns the monetization tier for the given Fandom context.
 *
 * @param fandomContext - The Fandom context object, which may be null.
 * @returns The monetization tier for the page, or {@link MonetizationTier.ERROR_NO_TIER_PROVIDED} if not set.
 *
 * @remarks
 * - If the monetization tier is not set in the context, {@link MonetizationTier.ERROR_NO_TIER_PROVIDED} is returned.
 * - Only tiers {@link MonetizationTier.PARTIALLY_MONETIZED} (3) and {@link MonetizationTier.FULLY_MONETIZED} (4) are considered monetized.
 * - Any other value will result in full demonetization of the page.
 *
 * @see MonetizationTier
 */
export function getMonetizationTier(
  fandomContext: FandomContext | null,
): MonetizationTier {
  const tier = fandomContext?.page?.monetizationTier;
  if (typeof tier === "number") {
    return tier.toString() as MonetizationTier;
  }
  return MonetizationTier.ERROR_NO_TIER_PROVIDED;
}
